import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'

import {
  bulkUpdateBetalingen, fetchFinancierbaar,
  type BetalingUpdate, type FactuurRegel,
} from '@/api/financierbaar'
import { KpiCard } from '@/components/KpiCard'
import { useCurrentOrg, useCurrentOrgId } from '@/hooks/useOrganisations'
import { euro } from '@/lib/format'

const STATUSSEN = ['Open', 'Betaald', 'Deelbetaling'] as const

interface RowEdit {
  betaalstatus: 'Open' | 'Betaald' | 'Deelbetaling'
  deelbetaling_bedrag: number
  factuurdatum: string | null
  vervaldatum: string | null
  betaaldatum: string | null
  dirty: boolean
}

function dateToInput(v: string | null | undefined): string {
  if (!v) return ''
  return v.slice(0, 10)
}

export function FinancierbaarPage() {
  const orgId = useCurrentOrgId()
  const org = useCurrentOrg()
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['financierbaar', orgId],
    queryFn: () => fetchFinancierbaar(orgId!),
    enabled: !!orgId,
  })

  // Lokale edit-state per factuur
  const [edits, setEdits] = useState<Record<string, RowEdit>>({})
  const [zoekFnr, setZoekFnr] = useState('')
  const [zoekCred, setZoekCred] = useState('')
  const [filterStatus, setFilterStatus] = useState<string[]>([...STATUSSEN])

  // Bij nieuwe data: synchroniseer alleen rijen die NIET dirty zijn
  useEffect(() => {
    if (!data) return
    setEdits((prev) => {
      const next: Record<string, RowEdit> = {}
      for (const r of data.facturen) {
        const bestaand = prev[r.inkoopfactuurnr]
        if (bestaand?.dirty) {
          next[r.inkoopfactuurnr] = bestaand
        } else {
          next[r.inkoopfactuurnr] = {
            betaalstatus: r.betaalstatus,
            deelbetaling_bedrag: r.deelbetaling_bedrag,
            factuurdatum: dateToInput(r.factuurdatum),
            vervaldatum: dateToInput(r.vervaldatum),
            betaaldatum: dateToInput(r.betaaldatum),
            dirty: false,
          }
        }
      }
      return next
    })
  }, [data])

  const facturen = data?.facturen ?? []

  // Apply edits → afgeleide totalen (live preview)
  const live = useMemo(() => {
    let waarde = 0, ded = 0, fin = 0
    const rows: (FactuurRegel & RowEdit & { liveDeductie: number; liveFinancierbaar: number; liveReden: string })[] = []
    for (const r of facturen) {
      const e = edits[r.inkoopfactuurnr]
      const status = e?.betaalstatus ?? r.betaalstatus
      const deel = e?.deelbetaling_bedrag ?? r.deelbetaling_bedrag
      let liveDed = 0, liveFin = 0, reden = ''
      if (status === 'Betaald') { liveFin = r.waarderingsom }
      else if (status === 'Deelbetaling') { liveFin = deel; liveDed = Math.max(0, r.waarderingsom - deel); reden = 'Restant na deelbetaling' }
      else { liveDed = r.waarderingsom; reden = 'Openstaande factuur' }
      waarde += r.waarderingsom; ded += liveDed; fin += liveFin
      rows.push({
        ...r,
        betaalstatus: status, deelbetaling_bedrag: deel,
        factuurdatum: e?.factuurdatum ?? dateToInput(r.factuurdatum),
        vervaldatum: e?.vervaldatum ?? dateToInput(r.vervaldatum),
        betaaldatum: e?.betaaldatum ?? dateToInput(r.betaaldatum),
        dirty: e?.dirty ?? false,
        liveDeductie: liveDed, liveFinancierbaar: liveFin, liveReden: reden,
      })
    }
    return { waarde, ded, fin, rows }
  }, [facturen, edits])

  // Filters
  const zichtbaar = useMemo(() => {
    return live.rows.filter((r) => {
      if (zoekFnr.trim() && !r.inkoopfactuurnr.toLowerCase().includes(zoekFnr.trim().toLowerCase())) return false
      if (zoekCred.trim() && !(r.crediteur ?? '').toLowerCase().includes(zoekCred.trim().toLowerCase())) return false
      if (filterStatus.length && !filterStatus.includes(r.betaalstatus)) return false
      return true
    })
  }, [live.rows, zoekFnr, zoekCred, filterStatus])

  const onopgeslagen = Object.values(edits).filter((e) => e.dirty).length

  const save = useMutation({
    mutationFn: () => {
      const wijzigingen: BetalingUpdate[] = []
      for (const r of facturen) {
        const e = edits[r.inkoopfactuurnr]
        if (!e?.dirty) continue
        wijzigingen.push({
          inkoopfactuurnummer: r.inkoopfactuurnr,
          betaalstatus: e.betaalstatus,
          deelbetaling_bedrag: e.betaalstatus === 'Deelbetaling' ? e.deelbetaling_bedrag : 0,
          factuurdatum: e.factuurdatum || null,
          vervaldatum: e.vervaldatum || null,
          betaaldatum: e.betaalstatus === 'Open' ? null : (e.betaaldatum || null),
        })
      }
      return bulkUpdateBetalingen(orgId!, wijzigingen)
    },
    onSuccess: (res) => {
      alert(`✓ ${res.gewijzigd} gewijzigd, ${res.nieuw} nieuw opgeslagen.`)
      qc.invalidateQueries({ queryKey: ['financierbaar', orgId] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setEdits({})
    },
    onError: (e: any) => alert('Fout: ' + (e?.response?.data?.detail ?? e.message)),
  })

  if (!orgId) return <Hdr><Box>Selecteer een klant in de zijbalk.</Box></Hdr>
  if (isLoading) return <Hdr subtitle={org?.naam}><Box>Laden…</Box></Hdr>
  if (isError) return <Hdr subtitle={org?.naam}>
    <Box><span className="text-red-600">Fout: {(error as Error).message}</span></Box>
  </Hdr>
  if (!data || data.facturen.length === 0) return <Hdr subtitle={org?.naam}>
    <Box>Nog geen inkoopfacturen. Voer eerst voorraad/emballage/verpakkingen/overig in met een inkoopfactuurnummer.</Box>
  </Hdr>

  const updateEdit = (fnr: string, patch: Partial<RowEdit>) => {
    setEdits((prev) => ({
      ...prev,
      [fnr]: { ...prev[fnr], ...patch, dirty: true },
    }))
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Financierbaar</h1>
        <p className="text-sm text-kh-gray-500 mt-1">
          {org?.naam} — inkoopfacturen, betaalstatus en financierbaarheid
        </p>
      </div>

      {/* KPI's */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Waarderingsom" value={euro(live.waarde)} />
        <KpiCard label="Deductiesom" value={euro(live.ded)} warning={live.ded > 0} accent="red" />
        <KpiCard label="Waarderingsom financierbaar" value={euro(live.fin)} />
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input className="input-field" placeholder="🔍 Zoek inkoopfactuurnummer"
               value={zoekFnr} onChange={(e) => setZoekFnr(e.target.value)} />
        <input className="input-field" placeholder="🔍 Zoek crediteur"
               value={zoekCred} onChange={(e) => setZoekCred(e.target.value)} />
        <div className="flex gap-2 items-center">
          {STATUSSEN.map((s) => (
            <label key={s} className="text-sm flex items-center gap-1">
              <input type="checkbox" checked={filterStatus.includes(s)}
                     onChange={(e) => setFilterStatus(e.target.checked
                       ? [...filterStatus, s]
                       : filterStatus.filter((x) => x !== s))} />
              {s}
            </label>
          ))}
        </div>
      </div>

      <p className="text-sm text-kh-gray-500 mb-2">{zichtbaar.length} van {data.facturen.length} facturen</p>

      {/* Tabel */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-kh-gray-50 text-kh-gray-600 uppercase text-xs tracking-wider">
            <tr>
              {['Factuurnr.', 'Crediteur', 'Voorraad', 'Emballage', 'Verpakk.', 'Overig', 'Waarderingsom',
                'Status', 'Deelbetaling', 'Factuurdatum', 'Vervaldatum', 'Betaaldatum',
                'Deductie', 'Financierbaar'].map((c) => (
                <th key={c} className="text-left px-3 py-3 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-kh-gray-100">
            {zichtbaar.map((r) => (
              <tr key={r.inkoopfactuurnr} className={r.dirty ? 'bg-yellow-50' : ''}>
                <td className="px-3 py-2 font-mono text-xs">{r.inkoopfactuurnr}</td>
                <td className="px-3 py-2 text-xs">{r.crediteur || ''}</td>
                <td className="px-3 py-2 text-right text-xs">{euro(r.voorraad)}</td>
                <td className="px-3 py-2 text-right text-xs">{euro(r.emballage)}</td>
                <td className="px-3 py-2 text-right text-xs">{euro(r.verpakkingen)}</td>
                <td className="px-3 py-2 text-right text-xs">{euro(r.overig)}</td>
                <td className="px-3 py-2 text-right font-medium">{euro(r.waarderingsom)}</td>

                <td className="px-3 py-2">
                  <select
                    value={r.betaalstatus}
                    onChange={(e) => updateEdit(r.inkoopfactuurnr, {
                      betaalstatus: e.target.value as RowEdit['betaalstatus']
                    })}
                    className="text-xs border border-kh-gray-300 rounded px-2 py-1 bg-white focus:border-kh-blue focus:outline-none">
                    {STATUSSEN.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>

                <td className="px-3 py-2">
                  <input type="number" step="0.01" min={0}
                         disabled={r.betaalstatus !== 'Deelbetaling'}
                         value={r.deelbetaling_bedrag || 0}
                         onChange={(e) => updateEdit(r.inkoopfactuurnr, {
                           deelbetaling_bedrag: Number(e.target.value)
                         })}
                         className="w-24 text-xs text-right border border-kh-gray-300 rounded px-2 py-1
                                    bg-white focus:border-kh-blue focus:outline-none
                                    disabled:bg-kh-gray-100 disabled:text-kh-gray-400" />
                </td>

                {(['factuurdatum', 'vervaldatum', 'betaaldatum'] as const).map((k) => (
                  <td key={k} className="px-3 py-2">
                    <input type="date" value={r[k] ?? ''}
                           disabled={k === 'betaaldatum' && r.betaalstatus === 'Open'}
                           onChange={(e) => updateEdit(r.inkoopfactuurnr, { [k]: e.target.value || null })}
                           className="text-xs border border-kh-gray-300 rounded px-2 py-1 bg-white
                                      focus:border-kh-blue focus:outline-none
                                      disabled:bg-kh-gray-100 disabled:text-kh-gray-400" />
                  </td>
                ))}

                <td className="px-3 py-2 text-right text-xs text-red-600">
                  {r.liveDeductie > 0 ? euro(r.liveDeductie) : ''}
                  {r.liveReden && <span className="block text-[10px] text-kh-gray-400">{r.liveReden}</span>}
                </td>
                <td className="px-3 py-2 text-right font-medium">{euro(r.liveFinancierbaar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save row */}
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary inline-flex items-center gap-2"
                disabled={onopgeslagen === 0 || save.isPending}
                onClick={() => save.mutate()}>
          <Save size={16} />
          {onopgeslagen === 0 ? '💾 Opgeslagen' : `💾 Wijzigingen opslaan (${onopgeslagen})`}
        </button>
        {onopgeslagen > 0 && (
          <span className="text-sm text-yellow-700">
            ⚠ {onopgeslagen} onopgeslagen wijziging{onopgeslagen === 1 ? '' : 'en'}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────
function Hdr({ subtitle, children }: { subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Financierbaar</h1>
        {subtitle && <p className="text-sm text-kh-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}
function Box({ children }: { children: React.ReactNode }) {
  return <div className="card p-6 text-center text-kh-gray-500">{children}</div>
}
